import { useMemo } from 'react';
import { remark } from 'remark';
import remarkHtml from 'remark-html';

// Utils
import cn from '../../../../utils/cn';
// Styles
import styles from './MarkdownCell.module.css';
import commonStyles from '../TableCellCommon.module.css';

/**
 * Markdown cell component for rendering markdown content in table cells
 * @param {object} props
 * @param {string} [props.value] - Markdown content to render
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder for empty values
 * @param {boolean} [props.inline=false] - Whether to render inline markdown
 * @param {Align} [props.align='left'] - Text alignment
 * @param {VerticalAlign} [props.verticalAlign='middle'] - Vertical alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @param {boolean} [props.strikeThrough=false] - Text with strike-through
 * @param {boolean} [props.underline=false] - Text underlined
 * @returns {JSX.Element}
 */
function MarkdownCell({
  value,
  className = '',
  placeholder = '-',
  inline = false,
  align = 'left',
  verticalAlign = 'middle',
  bold = false,
  italic = false,
  strikeThrough = false,
  underline = false,
}) {
  const htmlContent = useMemo(() => {
    if (!value || value === null || value === undefined || value === '') {
      return placeholder;
    }

    try {
      const processed = remark()
        .use(remarkHtml, { sanitize: true })
        .processSync(value);

      return processed.toString();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing markdown:', error);
      return value;
    }
  }, [value, placeholder]);

  const displayValue = value === null || value === undefined || value === '' ? placeholder : htmlContent;
  const isEmpty = value === null || value === undefined || value === '';

  const emptyContent = placeholder ? (
    <span className={styles['markdown-cell-empty']}>{placeholder}</span>
  ) : null;

  return (
    <div
      className={cn([
        styles['markdown-cell'],
        inline && styles['markdown-cell-inline'],
        className,
        bold && commonStyles['cell-bold'],
        italic && commonStyles['cell-italic'],
        strikeThrough && commonStyles['cell-strike-through'],
        underline && commonStyles['cell-underline'],
        align && commonStyles[`cell-align-${align}`],
        verticalAlign && commonStyles[`cell-vertical-align-${verticalAlign}`],
      ])}
    >
      {isEmpty ? emptyContent : (
        <div
          className={cn([
            styles['markdown-cell-content'],
            align === 'right' && commonStyles['cell-crop-start'],
          ])}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
      )}
    </div>
  );
}

export default MarkdownCell;
